import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@scio3/ui';
import { AlertTriangle, ChevronRight, Ellipsis, Folder } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MODES } from '@scio3/shared-types';

interface FolderBreadcrumbProps {
  currentPath: string;
  onNavigate: (path: string, teamId?: string) => void;
  hasWarning?: boolean;
  /** When true, shows "Shared with me" ancestor in the breadcrumb trail */
  fromShared?: boolean;
  /** Callback to navigate back to the shared root */
  onNavigateShared?: () => void;
}

export function FolderBreadcrumb({
  currentPath,
  onNavigate,
  hasWarning,
  fromShared,
  onNavigateShared,
}: FolderBreadcrumbProps) {
  const { t } = useTranslation();
  const isSharedWithMe = currentPath === MODES.SHARE_WITH_ME;

  if (isSharedWithMe) {
    return (
      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        <Button
          variant="ghost"
          size="sm"
          className="h-auto shrink-0 p-0 text-sm text-muted-foreground hover:bg-transparent hover:text-primary"
          onClick={() => onNavigate('/')}
        >
          {t('Home')}
        </Button>
        <ChevronRight className="h-3 w-3 shrink-0" />
        <span className="text-sm font-medium text-foreground">{t('Shared with me')}</span>
      </div>
    );
  }

  const segments = currentPath === '/' ? [] : currentPath.split('/').filter(Boolean);

  if (segments.length === 0) {
    return (
      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        <span className="text-sm font-medium text-foreground">{t('Home')}</span>
        {hasWarning && <AlertTriangle className="h-4 w-4 text-warning" />}
      </div>
    );
  }

  const showEllipsis = segments.length >= 3;
  const collapsedSegments = showEllipsis ? segments.slice(0, -1) : [];
  const parentSegment = segments.length === 2 ? segments[0] : null;
  const currentSegment = segments[segments.length - 1];

  return (
    <div className="flex items-center gap-1 overflow-hidden text-sm text-muted-foreground">
      {fromShared && onNavigateShared ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-auto shrink-0 p-0 text-sm text-muted-foreground hover:bg-transparent hover:text-primary"
          onClick={onNavigateShared}
        >
          {t('Shared with me')}
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          className="h-auto shrink-0 p-0 text-sm text-muted-foreground hover:bg-transparent hover:text-primary"
          onClick={() => onNavigate('/')}
        >
          {t('Home')}
        </Button>
      )}

      {showEllipsis && (
        <>
          <ChevronRight className="h-3 w-3 shrink-0" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 shrink-0 p-0"
                aria-label={t('Show parent folders')}
              >
                <Ellipsis className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {collapsedSegments.map((segment, i) => (
                <DropdownMenuItem
                  key={i}
                  onClick={() => {
                    const newPath = '/' + segments.slice(0, i + 1).join('/');
                    onNavigate(newPath);
                  }}
                >
                  <Folder className="mr-2 h-4 w-4" />
                  {segment}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}

      {parentSegment && (
        <>
          <ChevronRight className="h-3 w-3 shrink-0" />
          <Button
            variant="ghost"
            size="sm"
            className="h-auto min-w-0 shrink p-0 text-sm text-muted-foreground hover:bg-transparent hover:text-primary"
            onClick={() => onNavigate('/' + segments[0])}
          >
            <span className="max-w-[80px] truncate">{parentSegment}</span>
          </Button>
        </>
      )}

      <ChevronRight className="h-3 w-3 shrink-0" />
      <span className="min-w-0 truncate text-sm font-medium text-foreground">{currentSegment}</span>

      {hasWarning && <AlertTriangle className="ml-1 h-4 w-4 shrink-0 text-warning" />}
    </div>
  );
}
